import { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { db } from '@/lib/db';
import type { MemberPublic, Notification } from '@/lib/db/types';
import { notificationCopy } from '@/lib/notifications/copy';
import { timeAgo } from '@/lib/timeAgo';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';

interface Props {
  onClose: () => void | Promise<void>;
}

function renderNotificationText(n: Notification, actor?: MemberPublic | null) {
  const text = notificationCopy(n, actor);
  if (!actor?.name || !text.includes(actor.name)) {
    return text;
  }
  const [before, after] = text.split(actor.name);
  return (
    <>
      {before}
      <strong>{actor.name}</strong>
      {after}
    </>
  );
}

export function ActivitySheet({ onClose }: Props) {
  const [items, setItems] = useState<Notification[]>([]);
  const [actors, setActors] = useState<Record<string, MemberPublic>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const data = await db.notifications.list({ limit: 50 });
        if (!active) return;
        setItems(data);
        const actorIds = [...new Set(data.map((n) => n.actor_id).filter(Boolean))] as string[];
        if (actorIds.length) {
          const members = await Promise.all(actorIds.map((id) => db.members.get(id)));
          if (active) {
            setActors(Object.fromEntries(
              members.filter(Boolean).map((m) => [m!.id, m!]),
            ));
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const markAll = async () => {
    await db.notifications.markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
  };

  const handleClose = async () => {
    if (items.some((n) => !n.read_at)) {
      await db.notifications.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    }
    onClose();
  };

  return (
    <Sheet
      title="Activity"
      onClose={handleClose}
      headerRight={
        items.some((n) => !n.read_at) ? (
          <button type="button" onClick={() => void markAll()} className="text-xs font-semibold text-gold">Mark all read</button>
        ) : null
      }
    >
      <div className="space-y-1.5 px-4 pb-6 pt-2">
        {loading && <div className="text-sm text-mute">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl bg-paper p-8 text-center text-sm text-mute shadow-bt">
            No activity yet. Tag friends, run games, and the noise begins.
          </div>
        )}
        {items.map((n) => {
          const actor = n.actor_id ? actors[n.actor_id] : null;
          const unread = !n.read_at;
          return (
            <div
              key={n.id}
              className={`flex items-center gap-3 rounded-xl p-3.5 shadow-bt ${unread ? 'bg-gold/10' : 'bg-paper'}`}
            >
              {actor ? (
                <Avatar member={actor} size={36} />
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold text-navy">
                  <Trophy size={18} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] leading-snug text-ink">
                  {renderNotificationText(n, actor)}
                </div>
                <div className="mt-0.5 text-[11px] text-mute">{timeAgo(n.created_at)} ago</div>
              </div>
              {unread && <div className="h-2 w-2 shrink-0 rounded-full bg-gold" />}
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
